import { DistanceUnits } from '../types';

const METERS_PER_MILE = 1609.344;

export function formatDistance(meters: number, units: DistanceUnits): string {
  if (!Number.isFinite(meters)) return '—';
  if (units === 'mi') {
    const miles = meters / METERS_PER_MILE;
    if (miles < 0.1) return `${Math.round(meters * 3.28084)} ft`;
    return `${miles < 10 ? miles.toFixed(1) : Math.round(miles)} mi`;
  }
  if (meters < 1000) return `${Math.round(meters)} m`;
  const km = meters / 1000;
  return `${km < 10 ? km.toFixed(1) : Math.round(km)} km`;
}

export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  const total = Math.round(seconds / 60);
  if (total < 60) return `${total} min`;
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    return `${days} d ${hours % 24} hr`;
  }
  return minutes ? `${hours} hr ${minutes} min` : `${hours} hr`;
}

/** Signed delta against the primary route, e.g. "+7 min · −2.1 mi". */
export function formatDelta(
  deltaSeconds: number,
  deltaMeters: number,
  units: DistanceUnits,
): string {
  const minutes = Math.round(deltaSeconds / 60);
  const timePart = minutes === 0 ? 'same time' : `${minutes > 0 ? '+' : '−'}${Math.abs(minutes)} min`;
  const distance = formatDistance(Math.abs(deltaMeters), units);
  const distancePart = Math.abs(deltaMeters) < 50 ? 'same distance' : `${deltaMeters > 0 ? '+' : '−'}${distance}`;
  return `${timePart} · ${distancePart}`;
}

export function formatArrival(departAt: string | null, durationSeconds: number): string | null {
  const start = departAt ? new Date(departAt) : new Date();
  if (Number.isNaN(start.getTime())) return null;
  const arrival = new Date(start.getTime() + durationSeconds * 1000);
  return arrival.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export function formatCoord(longitude: number, latitude: number): string {
  return `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
}

/**
 * Parse operator-typed coordinates: "27.9, -82.4", "27.9 -82.4",
 * "27.9N 82.4W", or a pasted MGRS string.
 */
export function parseCoordinateInput(raw: string): { longitude: number; latitude: number } | null {
  const text = raw.trim();
  if (!text) return null;

  const dms = text.match(/^\s*(\d+(?:\.\d+)?)\s*([NS])[,\s]+(\d+(?:\.\d+)?)\s*([EW])\s*$/i);
  if (dms) {
    const lat = Number(dms[1]) * (dms[2].toUpperCase() === 'S' ? -1 : 1);
    const lon = Number(dms[3]) * (dms[4].toUpperCase() === 'W' ? -1 : 1);
    return inRange(lon, lat);
  }

  const pair = text.match(/^\s*(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?)\s*$/);
  if (pair) {
    return inRange(Number(pair[2]), Number(pair[1]));
  }

  const mgrs = parseMgrs(text);
  if (mgrs) return mgrs;

  return null;
}

function inRange(longitude: number, latitude: number): { longitude: number; latitude: number } | null {
  if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return null;
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null;
  return { longitude, latitude };
}

const MGRS_PATTERN = /^(\d{1,2})([C-HJ-NP-X])\s*([A-HJ-NP-Z])([A-HJ-NP-V])\s*(\d{2,10})$/i;
const SET_ORIGIN_ROW = [0, 0, 200000, 400000, 600000, 800000, 0];

/**
 * Minimal MGRS → lon/lat conversion (WGS-84) so operators can paste a grid
 * reference. Accurate to the precision of the supplied digits.
 */
export function parseMgrs(input: string): { longitude: number; latitude: number } | null {
  const match = input.replace(/\s+/g, ' ').trim().match(MGRS_PATTERN);
  if (!match) return null;
  const zone = Number(match[1]);
  const band = match[2].toUpperCase();
  const colLetter = match[3].toUpperCase();
  const rowLetter = match[4].toUpperCase();
  const digits = match[5];
  if (digits.length % 2 !== 0) return null;

  const half = digits.length / 2;
  const scale = 10 ** (5 - half);
  const easting = Number(digits.slice(0, half)) * scale;
  const northing = Number(digits.slice(half)) * scale;

  const colLetters = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const rowLetters = 'ABCDEFGHJKLMNPQRSTUV';
  const colIdx = colLetters.indexOf(colLetter);
  const rowIdx = rowLetters.indexOf(rowLetter);
  if (colIdx < 0 || rowIdx < 0) return null;

  const set = ((zone - 1) % 6) + 1;
  const colOrigin = ((set - 1) % 3) * 8;
  const e100k = ((colIdx - colOrigin + 24) % 24) * 100000 + 100000;
  const rowOrigin = SET_ORIGIN_ROW[set % 2 === 0 ? 1 : 0] || 0;
  let n100k = ((rowIdx - (set % 2 === 0 ? 5 : 0) + 20) % 20) * 100000 + rowOrigin;

  const bandLetters = 'CDEFGHJKLMNPQRSTUVWX';
  const bandIdx = bandLetters.indexOf(band);
  if (bandIdx < 0) return null;
  const approxNorthing = (bandIdx - 10) * 888960 + (band >= 'N' ? 0 : 10000000);
  while (n100k + northing < approxNorthing - 500000) n100k += 2000000;

  const utmEasting = e100k + easting;
  const utmNorthing = n100k + northing;
  return utmToLonLat(zone, band >= 'N', utmEasting, utmNorthing);
}

function utmToLonLat(zone: number, northernHemisphere: boolean, easting: number, northing: number) {
  const a = 6378137.0;
  const f = 1 / 298.257223563;
  const k0 = 0.9996;
  const e = Math.sqrt(f * (2 - f));
  const e1sq = (e * e) / (1 - e * e);

  const x = easting - 500000;
  const y = northernHemisphere ? northing : northing - 10000000;

  const m = y / k0;
  const mu = m / (a * (1 - (e * e) / 4 - (3 * e ** 4) / 64 - (5 * e ** 6) / 256));
  const e1 = (1 - Math.sqrt(1 - e * e)) / (1 + Math.sqrt(1 - e * e));

  const phi1 = mu
    + ((3 * e1) / 2 - (27 * e1 ** 3) / 32) * Math.sin(2 * mu)
    + ((21 * e1 ** 2) / 16 - (55 * e1 ** 4) / 32) * Math.sin(4 * mu)
    + ((151 * e1 ** 3) / 96) * Math.sin(6 * mu);

  const n1 = a / Math.sqrt(1 - (e * Math.sin(phi1)) ** 2);
  const t1 = Math.tan(phi1) ** 2;
  const c1 = e1sq * Math.cos(phi1) ** 2;
  const r1 = (a * (1 - e * e)) / (1 - (e * Math.sin(phi1)) ** 2) ** 1.5;
  const d = x / (n1 * k0);

  const latitude = phi1 - ((n1 * Math.tan(phi1)) / r1) * (
    (d * d) / 2
    - ((5 + 3 * t1 + 10 * c1 - 4 * c1 * c1 - 9 * e1sq) * d ** 4) / 24
    + ((61 + 90 * t1 + 298 * c1 + 45 * t1 * t1 - 252 * e1sq - 3 * c1 * c1) * d ** 6) / 720
  );
  const longitude = (
    d
    - ((1 + 2 * t1 + c1) * d ** 3) / 6
    + ((5 - 2 * c1 + 28 * t1 - 3 * c1 * c1 + 8 * e1sq + 24 * t1 * t1) * d ** 5) / 120
  ) / Math.cos(phi1);

  const lat = (latitude * 180) / Math.PI;
  const lon = (zone - 1) * 6 - 180 + 3 + (longitude * 180) / Math.PI;
  return inRange(lon, lat);
}
